import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

const SPORTS_LABEL = {
  tennis: "테니스", soccer: "축구", futsal: "풋살", badminton: "배드민턴",
  basketball: "농구", bowling: "볼링", table_tennis: "탁구", baseball: "야구",
  golf: "골프", pickleball: "피클볼", billiards: "당구",
};
const SPORTS_ICON = {
  tennis: "🎾", soccer: "⚽", futsal: "🥅", badminton: "🏸",
  basketball: "🏀", bowling: "🎳", table_tennis: "🏓", baseball: "⚾",
  golf: "⛳", pickleball: "🏸", billiards: "🎱",
};

function normalizeSportsType(type) {
  if (type === "tabletennis") return "table_tennis";
  if (type === "running")     return "pickleball";
  return type || "";
}

function formatSportDetail(rawType, cd) {
  if (!cd) return null;
  const t = normalizeSportsType(rawType);
  switch (t) {
    case "tennis":       return cd.ntrp ? `NTRP ${cd.ntrp}` : null;
    case "soccer":
    case "futsal":       return cd.position || null;
    case "badminton":    return cd.level || null;
    case "basketball":   return [cd.position, cd.level].filter(Boolean).join(" · ") || null;
    case "bowling":      return cd.avg ? `AVG ${cd.avg}` : null;
    case "table_tennis": return cd.level || null;
    case "baseball":     return cd.position || null;
    case "golf":         return cd.handicap ? `핸디캡 ${cd.handicap}` : null;
    case "pickleball":
    case "billiards":    return cd.years ? `구력 ${cd.years}년` : null;
    default:             return null;
  }
}

export default function TeamMemberPanel({ teamId, currentUserId, isLeader, showTransfer = false }) {
  const [members, setMembers]       = useState([]);
  const [pending, setPending]       = useState([]);
  const [myRecord, setMyRecord]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [teamSportsType, setTeamSportsType] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [modalProfile, setModalProfile]     = useState(null);
  const [modalSports, setModalSports]       = useState([]);
  const [modalLoading, setModalLoading]     = useState(false);

  const fetchMembers = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);

    const { data: rows } = await supabase
      .from("team_members")
      .select("id, user_id, role, status, nickname")
      .eq("team_id", teamId);

    const allRows = rows || [];

    // nickname 없는 행은 user_profiles에서 보완
    const missingIds = [...new Set(allRows.filter(r => !r.nickname).map(r => r.user_id))];
    let upNickMap = {};
    if (missingIds.length > 0) {
      const { data: upData } = await supabase
        .from("user_profiles")
        .select("id, nickname")
        .in("id", missingIds);
      upNickMap = Object.fromEntries((upData || []).map(p => [p.id, p.nickname]));
    }

    const enriched = allRows.map(r => ({
      ...r,
      resolvedNick: r.nickname || upNickMap[r.user_id] || "알 수 없음",
    }));

    setMembers(enriched.filter((r) => r.status === "accepted"));
    setPending(enriched.filter((r) => r.status === "pending"));
    setMyRecord(enriched.find((r) => r.user_id === currentUserId) || null);
    setLoading(false);
  }, [teamId, currentUserId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // 팀 종목 조회 (모달 종목 필터용)
  useEffect(() => {
    if (!teamId) return;
    supabase.from("teams").select("sports_type").eq("id", teamId).maybeSingle()
      .then(({ data }) => { if (data) setTeamSportsType(data.sports_type); });
  }, [teamId]);

  // 팀원 클릭 시 프로필 + 해당 종목 로드
  useEffect(() => {
    if (!selectedMember) return;
    const load = async () => {
      setModalLoading(true);
      const normalType = normalizeSportsType(teamSportsType || "");
      let sportQuery = supabase.from("user_sports").select("*").eq("user_id", selectedMember.user_id);
      if (normalType) sportQuery = sportQuery.eq("sports_type", normalType);
      const [{ data: fp }, { data: sp }] = await Promise.all([
        supabase.from("user_profiles").select("*").eq("id", selectedMember.user_id).maybeSingle(),
        sportQuery,
      ]);
      setModalProfile(fp || null);
      setModalSports(sp || []);
      setModalLoading(false);
    };
    load();
  }, [selectedMember]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAccept = async (id) => {
    setActionLoading(id);
    await supabase.from("team_members").update({ status: "accepted" }).eq("id", id);
    setActionLoading(null);
    fetchMembers();
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    await supabase.from("team_members").update({ status: "rejected" }).eq("id", id);
    setActionLoading(null);
    fetchMembers();
  };

  // 팀 나가기 (본인)
  const handleLeave = async () => {
    if (!window.confirm("정말 이 팀에서 나가시겠어요?")) return;
    setActionLoading("leave");
    await supabase.from("team_members").delete().eq("team_id", teamId).eq("user_id", currentUserId);
    setActionLoading(null);
    fetchMembers();
  };

  // 팀원 강퇴 (팀장)
  const handleKick = async (m) => {
    if (!window.confirm(`${m.resolvedNick || "이 팀원"}을 정말 내보내시겠어요?`)) return;
    setActionLoading(m.id);
    await supabase.from("team_members").delete().eq("id", m.id);
    setActionLoading(null);
    fetchMembers();
  };

  // 팀장 양도 버튼 클릭 → 확인 모달 열기
  const handleTransferLeader = (member) => {
    setTransferTarget(member);
  };

  // 확인 모달에서 [양도하기] 클릭 → 순차 실행
  const handleConfirmTransfer = async () => {
    if (!transferTarget) return;
    setActionLoading(transferTarget.id);

    // 1단계: 기존 팀장 role → member
    const { error: e1 } = await supabase
      .from("team_members").update({ role: "member" })
      .eq("team_id", teamId).eq("user_id", currentUserId);
    if (e1) console.error("[handleConfirmTransfer] 기존팀장 role 변경 실패:", e1);

    // 2단계: 새 팀장 role → leader
    const { error: e2 } = await supabase
      .from("team_members").update({ role: "leader" })
      .eq("id", transferTarget.id);
    if (e2) console.error("[handleConfirmTransfer] 새팀장 role 변경 실패:", e2);

    // 3단계: teams.user_id 갱신
    const { error: e3 } = await supabase
      .from("teams").update({ user_id: transferTarget.user_id })
      .eq("id", teamId);
    if (e3) {
      console.error("[handleConfirmTransfer] teams.user_id 갱신 실패:", e3);
      alert("팀 소유자 변경 중 오류가 발생했어요. 다시 시도해주세요.");
    }

    setActionLoading(null);
    setTransferTarget(null);
    fetchMembers();
  };

  if (loading) {
    return (
      <div style={s.card}>
        <p style={s.loadingText}>불러오는 중...</p>
      </div>
    );
  }

  return (
    <>
    <div style={s.card}>
      {/* 팀원 목록 */}
      <h3 style={s.sectionTitle}>팀원 ({members.length}명)</h3>
      {members.length === 0 ? (
        <p style={s.emptyText}>아직 팀원이 없어요.</p>
      ) : (
        members.map((m) => {
          const nick = m.resolvedNick || "(닉네임 없음)";
          const isMe = m.user_id === currentUserId;
          const isLeaderRole = m.role === "leader";
          return (
            <div key={m.id} style={{ ...s.row, cursor: "pointer" }} onClick={() => setSelectedMember(m)}>
              <div style={s.rowLeft}>
                <span style={s.nick}>{nick}</span>
                {isLeaderRole && <span style={s.leaderBadge}>팀장</span>}
                {isMe && <span style={s.meBadge}>나</span>}
              </div>
              {showTransfer && isLeader && !isMe && !isLeaderRole && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    style={{ ...s.outlineBtn, opacity: actionLoading === m.id ? 0.5 : 1 }}
                    onClick={e => { e.stopPropagation(); handleTransferLeader(m); }}
                    disabled={actionLoading === m.id}
                  >
                    팀장 양도
                  </button>
                  <button
                    style={{ ...s.kickBtn, opacity: actionLoading === m.id ? 0.5 : 1 }}
                    onClick={e => { e.stopPropagation(); handleKick(m); }}
                    disabled={actionLoading === m.id}
                  >
                    강퇴
                  </button>
                </div>
              )}
              {isMe && (
                <button
                  style={{ ...s.leaveBtn, opacity: actionLoading === "leave" ? 0.5 : 1 }}
                  onClick={e => {
                    e.stopPropagation();
                    if (isLeaderRole) {
                      alert("팀장은 먼저 팀장 권한을 다른 팀원에게 넘긴 후 나갈 수 있어요.");
                    } else {
                      handleLeave();
                    }
                  }}
                  disabled={actionLoading === "leave"}
                >
                  나가기
                </button>
              )}
            </div>
          );
        })
      )}

      {/* 가입신청 목록 (팀장만) */}
      {isLeader && pending.length > 0 && (
        <>
          <h3 style={{ ...s.sectionTitle, marginTop: 20 }}>가입신청 ({pending.length}건)</h3>
          {pending.map((m) => {
            const nick = m.resolvedNick || "(닉네임 없음)";
            const busy = actionLoading === m.id;
            return (
              <div key={m.id} style={s.row}>
                <span style={s.nick}>{nick}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    style={{ ...s.acceptBtn, opacity: busy ? 0.5 : 1 }}
                    onClick={() => handleAccept(m.id)}
                    disabled={busy}
                  >
                    수락
                  </button>
                  <button
                    style={{ ...s.rejectBtn, opacity: busy ? 0.5 : 1 }}
                    onClick={() => handleReject(m.id)}
                    disabled={busy}
                  >
                    거절
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* 신청 대기 중 안내 */}
      {!isLeader && myRecord && myRecord.status === "pending" && (
        <p style={s.pendingText}>가입 승인 대기 중이에요.</p>
      )}
    </div>
    {selectedMember && (
      <MemberProfileModal
        member={selectedMember}
        profile={modalProfile}
        sports={modalSports}
        loading={modalLoading}
        onClose={() => setSelectedMember(null)}
      />
    )}
    {transferTarget && (
      <ConfirmTransferModal
        member={transferTarget}
        loading={actionLoading === transferTarget.id}
        onConfirm={handleConfirmTransfer}
        onCancel={() => setTransferTarget(null)}
      />
    )}
    </>
  );
}

function ConfirmTransferModal({ member, loading, onConfirm, onCancel }) {
  const nick = member.resolvedNick || "이 멤버";
  return (
    <div style={cm.backdrop}>
      <div style={cm.card}>
        <div style={cm.title}>팀장 양도</div>
        <div style={cm.body}>
          정말 <strong>{nick}</strong>님에게<br />팀장을 양도하시겠습니까?
        </div>
        <div style={cm.btnRow}>
          <button style={cm.cancelBtn} onClick={onCancel} disabled={loading}>취소</button>
          <button style={{ ...cm.confirmBtn, opacity: loading ? 0.6 : 1 }} onClick={onConfirm} disabled={loading}>
            {loading ? "처리 중..." : "양도하기"}
          </button>
        </div>
      </div>
    </div>
  );
}

const cm = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 400, padding: "0 20px",
  },
  card: {
    background: "#fff", borderRadius: 14,
    width: "100%", maxWidth: 320,
    padding: "24px 20px 20px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  title: {
    fontSize: 16, fontWeight: 700, color: "#111827",
    marginBottom: 12, textAlign: "center",
  },
  body: {
    fontSize: 14, color: "#374151", lineHeight: 1.7,
    textAlign: "center", marginBottom: 20,
  },
  btnRow: { display: "flex", gap: 8 },
  cancelBtn: {
    flex: 1, padding: "12px", background: "#f3f4f6", color: "#6b7280",
    border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit",
  },
  confirmBtn: {
    flex: 1, padding: "12px", background: "#E8412A", color: "#fff",
    border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
  },
};

function ModalInfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

function MemberProfileModal({ member, profile, sports, loading, onClose }) {
  const nick       = profile?.nickname || member.resolvedNick || "(닉네임 없음)";
  const initials   = nick[0]?.toUpperCase() || "?";
  const genderChip = profile?.gender === "male" ? "남성" : profile?.gender === "female" ? "여성" : null;
  const genderFull = profile?.gender === "male" ? "남자" : profile?.gender === "female" ? "여자" : null;
  const sp         = sports[0] || null;
  const spLabel    = sp ? (SPORTS_LABEL[sp.sports_type] || sp.sports_type) : null;
  const spIcon     = sp ? (SPORTS_ICON[sp.sports_type] || "🏅") : null;
  const spLevel    = sp ? formatSportDetail(sp.sports_type, sp.career_detail) : null;

  return (
    <div style={ms.backdrop} onClick={onClose}>
      <div style={ms.sheet} onClick={e => e.stopPropagation()}>
        <button style={ms.closeBtn} onClick={onClose}>✕</button>

        {/* 상단 프로필 */}
        <div style={ms.profileTop}>
          {profile?.profile_image ? (
            <img src={profile.profile_image} alt="profile" style={ms.avatar} />
          ) : (
            <div style={ms.avatarFallback}>
              <span style={ms.avatarInitials}>{initials}</span>
            </div>
          )}
          <div style={ms.nickName}>{nick}</div>
          <div style={ms.metaRow}>
            {genderChip  && <span style={ms.metaChip}>{genderChip}</span>}
            {profile?.region && <span style={ms.metaChip}>📍 {profile.region}</span>}
          </div>
          {profile?.awards && <div style={ms.awards}>🏆 {profile.awards}</div>}
        </div>

        {/* 선수 정보 */}
        <div style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", marginBottom: 10, letterSpacing: "0.02em" }}>선수 정보</div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
            <div style={ms.spinner} />
          </div>
        ) : !sp ? (
          <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>등록된 종목 프로필이 없어요</div>
        ) : (
          <div style={{ background: "#F5F5F7", borderRadius: 10, padding: "0 14px" }}>
            <ModalInfoRow label="종목" value={`${spIcon} ${spLabel}`} />
            {spLevel && <ModalInfoRow label="레벨" value={spLevel} />}
            <ModalInfoRow label="선수 출신" value={sp.is_player ? "선출" : "비선출"} />
            {genderFull && <ModalInfoRow label="성별" value={genderFull} />}
          </div>
        )}
      </div>
    </div>
  );
}

const ms = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 300, padding: "0 16px",
  },
  sheet: {
    background: "#fff", borderRadius: 16,
    width: "100%", maxWidth: 340,
    padding: "24px 20px",
    position: "relative",
    maxHeight: "80vh", overflowY: "auto",
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  closeBtn: {
    position: "absolute", top: 16, right: 16,
    background: "none", border: "none",
    fontSize: 18, color: "#6b7280", cursor: "pointer", lineHeight: 1,
  },
  profileTop: {
    display: "flex", flexDirection: "column", alignItems: "center",
    paddingBottom: 20, borderBottom: "1px solid #f3f4f6", marginBottom: 16,
  },
  avatar: { width: 72, height: 72, borderRadius: "50%", objectFit: "cover", marginBottom: 12 },
  avatarFallback: {
    width: 72, height: 72, borderRadius: "50%", background: "#E8412A",
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12,
  },
  avatarInitials: { fontSize: 28, fontWeight: 800, color: "#fff" },
  nickName: { fontSize: 20, fontWeight: 800, color: "#1A1A2E", marginBottom: 8 },
  metaRow: { display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", marginBottom: 4 },
  metaChip: {
    padding: "3px 10px", background: "#F5F5F7", borderRadius: 999,
    fontSize: 12, color: "#374151", fontWeight: 500,
  },
  awards: { fontSize: 12, color: "#d97706", marginTop: 6 },
  spinner: {
    width: 24, height: 24,
    border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A",
    borderRadius: "50%", animation: "spin 0.8s linear infinite",
  },
};

const s = {
  card: {
    background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
    padding: "20px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  sectionTitle: {
    margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: "#111827",
  },
  loadingText: {
    fontSize: 14, color: "#9ca3af", textAlign: "center", padding: "20px 0",
  },
  emptyText: {
    fontSize: 13, color: "#9ca3af", margin: 0,
  },
  row: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 0", borderBottom: "1px solid #f3f4f6",
  },
  rowLeft: {
    display: "flex", alignItems: "center", gap: 8,
  },
  nick: {
    fontSize: 14, fontWeight: 600, color: "#111827",
  },
  leaderBadge: {
    fontSize: 11, fontWeight: 700, color: "#E24B4A",
    background: "#fef2f2", borderRadius: 6, padding: "2px 6px",
  },
  meBadge: {
    fontSize: 11, fontWeight: 700, color: "#6b7280",
    background: "#f3f4f6", borderRadius: 6, padding: "2px 6px",
  },
  outlineBtn: {
    padding: "6px 12px", background: "#fff",
    border: "1.5px solid #E24B4A", borderRadius: 8,
    fontSize: 12, fontWeight: 700, color: "#E24B4A",
    cursor: "pointer", fontFamily: "inherit",
  },
  kickBtn: {
    padding: "6px 12px", background: "#E24B4A",
    border: "none", borderRadius: 8,
    fontSize: 12, fontWeight: 700, color: "#fff",
    cursor: "pointer", fontFamily: "inherit",
  },
  leaveBtn: {
    padding: "6px 12px", background: "#fff",
    border: "1.5px solid #9ca3af", borderRadius: 8,
    fontSize: 12, fontWeight: 700, color: "#6b7280",
    cursor: "pointer", fontFamily: "inherit",
  },
  acceptBtn: {
    padding: "6px 14px", background: "#E24B4A",
    border: "none", borderRadius: 8,
    fontSize: 12, fontWeight: 700, color: "#fff",
    cursor: "pointer", fontFamily: "inherit",
  },
  rejectBtn: {
    padding: "6px 14px", background: "#fff",
    border: "1.5px solid #d1d5db", borderRadius: 8,
    fontSize: 12, fontWeight: 700, color: "#6b7280",
    cursor: "pointer", fontFamily: "inherit",
  },
  pendingText: {
    fontSize: 13, color: "#f59e0b", fontWeight: 600,
    textAlign: "center", marginTop: 16,
  },
};
